declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
    gtag?: (...args: unknown[]) => void;
  }
}

type DataLayerEntry = Record<string, unknown> | IArguments;

const DEFAULT_GA_MEASUREMENT_ID = 'G-853VGN9ZWR';
const GOOGLE_ANALYTICS_SCRIPT_ID = 'returnem-google-analytics';
const SENSITIVE_EVENT_KEYS = new Set(['bookingId', 'correlationId', 'email', 'phone', 'receiptId', 'returnId']);

type AnalyticsPage =
  | 'landing'
  | 'form'
  | 'review'
  | 'confirmation'
  | 'manage'
  | 'sign_in'
  | 'account_returns'
  | 'sell_donate'
  | 'how_it_works'
  | 'refund_policy'
  | 'privacy_policy';

function normalizeMeasurementId(rawValue: string | undefined): string | null {
  if (!rawValue) {
    return null;
  }

  const normalized = rawValue.trim().replace(/^['"]+|['"]+$/g, '');
  return normalized || null;
}

function getMeasurementId(): string | null {
  return normalizeMeasurementId(process.env.REACT_APP_GA_MEASUREMENT_ID) ?? DEFAULT_GA_MEASUREMENT_ID;
}

function ensureDataLayer(): DataLayerEntry[] {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  return window.dataLayer;
}

function sanitizeEventData(data: Record<string, unknown>): Record<string, string | number | boolean> {
  const sanitized: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_EVENT_KEYS.has(key) || value == null) {
      continue;
    }

    if (typeof value === 'string' || typeof value === 'boolean') {
      sanitized[key] = value;
      continue;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

function pushToDataLayer(entry: Record<string, unknown>): void {
  ensureDataLayer().push({
    ...entry,
    timestamp: new Date().toISOString(),
  });
}

function resolveGoogleAnalyticsEventName(event: string): string {
  return event === 'page_view' ? 'returnem_page_view' : event;
}

export function initializeAnalytics(): void {
  const measurementId = getMeasurementId();

  if (!measurementId || typeof document === 'undefined') {
    return;
  }

  ensureDataLayer();

  if (!window.gtag) {
    window.gtag = function gtag(): void {
      ensureDataLayer().push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', measurementId, { send_page_view: false });
  }

  if (document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_ANALYTICS_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function trackEvent(event: string, data: Record<string, unknown> = {}): void {
  const sanitizedData = sanitizeEventData(data);
  const payload = {
    event,
    ...sanitizedData,
  };

  pushToDataLayer(payload);

  if (window.gtag) {
    window.gtag('event', resolveGoogleAnalyticsEventName(event), sanitizedData);
  }
}

export function trackRouteView(path: string): void {
  const normalizedPath = path.trim() || '/';

  pushToDataLayer({
    event: 'route_view',
    path: normalizedPath,
  });

  if (window.gtag) {
    const pageLocation = `${window.location.origin}${normalizedPath}`;
    window.gtag('event', 'page_view', {
      page_location: pageLocation,
      page_path: normalizedPath,
      page_title: document.title,
    });
  }
}

export function trackPage(page: AnalyticsPage): void {
  trackEvent('page_view', { page });
}
